// import { Edit2, Trash2, ExternalLink, Copy } from "lucide-react";
import { momentum } from "ldrs";
import { useWebsitesStore } from "../../store/websitesStore";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
// import { Website } from "../../interfaces/websites";
import { decodeTokenService } from "../../services/decodeTokenService";
import { enqueueSnackbar } from "notistack";
import { deleteWebsite } from "../../services/generativeAI/generativeAIAxios/generateCopysAxios";
import ConfirmationDialog from "../payment/ConfirmationDialog";
import gifs from "../../assets/icons/gifs.json";
import Modal from "../payment/modal";
import { duplicateFunnel, getSectionsGenerated } from "../../services/funnels";
import { FunnelsCard } from "../cards/FunnelsCard";
import FunnelCardSkeleton from "../funnel/FunnelCardSkeleton";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { getSubDomain } from "../../services/subDomain/subDomain";
import { useTutorialContext } from "../../context/TutorialContext";
momentum.register();

type WebsitesGalleryProps = {};

interface SectionsGenerated {
  done: boolean;
  website_id: string;
}

const WebsitesGallery: React.FC<WebsitesGalleryProps> = () => {
  const { language } = useAuth();
  const { websites, isLoading, initialize } = useWebsitesStore();
  const [websiteIdToDelete, setWebsiteIdToDelete] = useState<string>("");
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState<string | null>(null);
  const [sectionsGenerated, setSectionsGenerated] = useState<
    SectionsGenerated[]
  >([]);

  const navigate = useNavigate();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleOpenModal = (id: string) => {
    setOpenModalDelete(true);
    setWebsiteIdToDelete(id);
  };

  const handleDelete = (id: string) => {
    setLoadingDelete(true);
    const userId = decodeTokenService.getUserIdFromToken() || "";
    deleteWebsite(userId, id)
      .then(() => {
        enqueueSnackbar("Funnel deleted successfully.", {
          variant: "success",
        });
        setTimeout(() => {
          setOpenModalDelete(false);
          window.location.reload();
        }, 1000);
      })
      .catch((error) => {
        console.error("Error deleting funnel:", error);
        enqueueSnackbar("Error deleting funnel.", {
          variant: "error",
        });
      })
      .finally(() => {
        setLoadingDelete(false);
      });
  };

  const handleEdit = async (websiteId: string) => {
    try {
      const url = handleUrlBuilder(websiteId);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Failed to fetch SSO URL:", error);
      toast.error("Error al cargar la página.");
    }
  };

  const handlePreview = async (websiteId: string) => {
    try {
      const subDomain = await getSubDomain(websiteId);
      if (subDomain.subdomain) {
        window.open(
          `https://${subDomain.subdomain}.${import.meta.env.VITE_DOMIAN_FUNNELS
          }`,
          "_blank"
        );
        return;
      }
    } catch (error) {
      const url = handleUrlPreview(websiteId);
      window.open(url, "_blank");
    }
  };

  const handleUrlBuilder = (websiteId: string) => {
    const userId = decodeTokenService.getUserIdFromToken() || "";
    const token = localStorage.getItem("access_token") || "";
    const url =
      import.meta.env.VITE_REACT_APP_URL_BUILDER +
      `builder/funnel/${userId}/${websiteId}?token=${token}`;
    return url;
  };

  const handleUrlPreview = (websiteId: string) => {
    const userId = decodeTokenService.getUserIdFromToken() || "";
    const url =
      import.meta.env.VITE_REACT_APP_URL_BUILDER +
      `user/${userId}/funnel/${websiteId}`;
    return url;
  };

  const handleDuplicate = (websiteId: string) => {
    setLoadingDuplicate(websiteId);
    duplicateFunnel(websiteId)
      .then((response) => {
        enqueueSnackbar("Funnel duplicated successfully.", {
          variant: "success",
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch((error) => {
        enqueueSnackbar("Error duplicating funnel.", {
          variant: "error",
        });
      });
  };

  const handleAdsClick = (websiteId: string) => {
    navigate(`/hyphens?funnelId=${websiteId}`);
  };

  const handleSectionsGenerated = () => {
    const data = websites.map((website) => website.websiteId);
    getSectionsGenerated(data)
      .then((response) => {
        if (response.length > 0) {
          setSectionsGenerated(response);
        }
      })
      .catch((error) => {
        console.error("Error fetching sections generated:", error);
      });
  };

  useEffect(() => {
    if (!isLoading) {
      handleSectionsGenerated();
    }
  }, [isLoading]);

  const websitesWithGenerated = websites.map((website) => {
    const match = sectionsGenerated.find(
      (section) => section.website_id === website.websiteId
    );

    return {
      ...website,
      generated: match ? match.done : false,
    };
  });

  const { isPaused, setCurrentStepIndex, resumeTutorial } = useTutorialContext();

  useEffect(() => {
    const checkIfPaused = async () => {
      if (websitesWithGenerated.length > 0 && !isLoading && isPaused) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setCurrentStepIndex(5);
        resumeTutorial();
      }
    };
    checkIfPaused();
  }, [websitesWithGenerated, !isLoading]);

  return (
    <div className="w-full">
      {openModalDelete && (
        <Modal
          onClose={() => {
            setOpenModalDelete(false);
          }}
        >
          <ConfirmationDialog
            loading={!loadingDelete}
            title={
              dictionary["are-you-sure-you-want-to-delete-this-funnel"][
              language
              ]
            }
            description={dictionary["this-action-cannot-be-undone"][language]}
            actionText={dictionary["delete"][language]}
            handleAction={() => {
              handleDelete(websiteIdToDelete);
            }}
            handleCancel={() => {
              setOpenModalDelete(false);
            }}
            cancelText="Cancel"
            imageSrc={gifs.delete}
          />
        </Modal>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading
          ? Array.from({ length: 9 }).map((_, index) => (
            <FunnelCardSkeleton key={index} />
          ))
          : websitesWithGenerated.map((website, index) => (
            <FunnelsCard
              key={website.websiteId}
              id={website.websiteId}
              idHtml={index === 0 ? "summaryTutorialCreateSalesPage-target" : ""}
              idSequence={website.idSequence}
              product={website.products}
              description={website.sales_angle_description}
              isActive={website.configurations.enabled}
              createdAt={website.createdAt}
              handleDuplicate={() => handleDuplicate(website.websiteId)}
              handleEdit={() => handleEdit(website.websiteId)}
              handleOpenModal={() => handleOpenModal(website.websiteId)}
              handlePreview={() => handlePreview(website.websiteId)}
              handleAdsClick={() => handleAdsClick(website.websiteId)}
              sectionsGenerated={website.generated}
            />
          ))}
      </div>
    </div>
  );
};

export default WebsitesGallery;
